import React from "react";
import PropTypes from "prop-types";

import { Column, Field, Control, Label, Input } from "rbx";

import {
  StateSelect,
  BooleanInput,
  CurrencyInput,
  DateInput,
  PhoneNumberInput,
  SystemCodeSelect,
  PaymentTermSelect,
  GLMainAccountSelect
} from "../../../../components/inputs";
import { useAllowCustomerNoChange } from "../../../../hooks";

const Details = ({ editing, customer, onChange, assetCode, revenueCode }) => {
  const isAllowedCustomerNoEdit = useAllowCustomerNoChange(customer.RecId);

  return (
    <React.Fragment>
      <Column.Group>
        <Column size="one-fifth">
          <Field
            tooltip={
              !isAllowedCustomerNoEdit && "Customer # cannot be modified."
            }
          >
            <Control>
              <Label>Customer #</Label>
              <Input
                required
                autocomplete="new"
                disabled={!isAllowedCustomerNoEdit}
                name="CustomerNo"
                readOnly={!editing}
                type="text"
                value={customer.CustomerNo}
                onChange={onChange}
              />
            </Control>
          </Field>
        </Column>
        <Column size="two-fifths">
          <Field>
            <Control>
              <Label>Name</Label>
              <Input
                required
                autocomplete="new"
                name="Name"
                readOnly={!editing}
                value={customer?.Name}
                onChange={onChange}
              />
            </Control>
          </Field>
        </Column>
        <Column>
          <Field>
            <Control>
              <SystemCodeSelect
                icon="globe"
                label="Customer Group"
                lookup={{ Category: "Customer", Code: "CustomerType" }}
                name="CustomerTypeRecIdRef"
                readOnly={!editing}
                value={customer?.CustomerTypeRecIdRef}
                onChange={onChange}
              />
            </Control>
          </Field>
        </Column>
        <Column>
          <Field kind="group">
            <Control expanded>
              <Label>Bill To</Label>
              <BooleanInput
                name="BillToCust"
                readOnly={!editing}
                value={customer.BillToCust}
                onChange={onChange}
              />
            </Control>
            <Control expanded>
              <Label>Order</Label>
              <BooleanInput
                name="OrderCust"
                readOnly={!editing}
                value={!!customer.OrderCust}
                onChange={onChange}
              />
            </Control>
            <Control expanded>
              <Label>Active</Label>
              <BooleanInput
                name="Active"
                readOnly={!editing}
                value={customer?.Active}
                onChange={onChange}
              />
            </Control>
          </Field>
        </Column>
      </Column.Group>
      <Column.Group>
        <Column size="one-fifth">
          <Field>
            <Control>
              <Label>Address 1</Label>
              <Input
                autocomplete="new"
                name="Address1"
                readOnly={!editing}
                value={customer?.Address1}
                onChange={onChange}
              />
            </Control>
          </Field>
        </Column>
        <Column size="two-fifths">
          <Field kind="group">
            <Control expanded>
              <Label>Address 2</Label>
              <Input
                autocomplete="new"
                name="Address2"
                readOnly={!editing}
                value={customer?.Address2}
                onChange={onChange}
              />
            </Control>
            <Control expanded>
              <Label>City</Label>
              <Input
                autocomplete="new"
                name="City"
                readOnly={!editing}
                value={customer?.City}
                onChange={onChange}
              />
            </Control>
          </Field>
        </Column>
        <Column size="two-fifths">
          <Field kind="group">
            <Control expanded>
              <Label>State</Label>
              <StateSelect
                name="State"
                readOnly={!editing}
                value={customer?.State}
                onChange={onChange}
              />
            </Control>
            <Control expanded>
              <Label>Zip</Label>
              <Input
                autocomplete="new"
                name="Zip"
                readOnly={!editing}
                value={customer?.Zip}
                onChange={onChange}
              />
            </Control>
            <Control>
              <PaymentTermSelect
                required
                disabled={!editing || !customer?.BillToCust}
                label="Payment Term"
                name="PaymentTermsRecIdRef"
                value={customer?.PaymentTermsRecIdRef}
                where={{ Active: true }}
                onChange={onChange}
              />
            </Control>
            <Control expanded>
              <SystemCodeSelect
                required
                icon="globe"
                label="ELD Status"
                lookup={{ Category: "ELD", Code: "ELDStatus" }}
                name="ELDStatusRecIdRef"
                readOnly={!editing || !customer.OrderCust}
                value={customer.ELDStatusRecIdRef}
                onChange={onChange}
              />
            </Control>
          </Field>
        </Column>
      </Column.Group>
      <Column.Group>
        <Column>
          <Field>
            <Control>
              <Label>Billing Contact</Label>
              <Input
                autocomplete="new"
                name="BillingContactName"
                readOnly={!editing}
                value={customer?.BillingContactName}
                onChange={onChange}
              />
            </Control>
          </Field>
        </Column>
        <Column>
          <Field>
            <Control>
              <Label>Phone #</Label>
              <PhoneNumberInput
                name="Telephone"
                readOnly={!editing}
                value={customer?.Telephone}
                onChange={onChange}
              />
            </Control>
          </Field>
        </Column>
        <Column>
          <Field>
            <Control>
              <Label>Fax #</Label>
              <PhoneNumberInput
                name="FaxNo"
                readOnly={!editing}
                value={customer?.FaxNo}
                onChange={onChange}
              />
            </Control>
          </Field>
        </Column>
        <Column>
          <Control expanded>
            <GLMainAccountSelect
              disabled={!editing}
              label="Revenue GL Override"
              name="RevGLRecIdRef"
              value={customer?.RevGLRecIdRef}
              where={{
                TypeRecIdRef: revenueCode.RecId
              }}
              onChange={onChange}
            />
          </Control>
        </Column>
        <Column>
          <Control expanded>
            <GLMainAccountSelect
              disabled={!editing}
              label="AR GL Override"
              name="ArGLRecIdRef"
              value={customer?.ArGLRecIdRef}
              where={{
                TypeRecIdRef: assetCode.RecId
              }}
              onChange={onChange}
            />
          </Control>
        </Column>
      </Column.Group>
      <Column.Group>
        <Column size="one-fifth">
          <Field kind="group">
            <Control>
              <Label>Last Sale Date</Label>
              <DateInput
                readOnly
                name="LastSaleDate"
                value={customer?.LastSaleDate}
                onChange={onChange}
              />
            </Control>
            <Control>
              <Label>Last Sale Amount</Label>
              <CurrencyInput
                readOnly
                name="LastSaleAmount"
                value={customer?.LastSaleAmount}
                onChange={onChange}
              />
            </Control>
          </Field>
        </Column>
      </Column.Group>
    </React.Fragment>
  );
};

Details.propTypes = {
  editing: PropTypes.bool,
  customer: PropTypes.object,
  onChange: PropTypes.func,
  assetCode: PropTypes.object,
  revenueCode: PropTypes.object
};

PropTypes.defaultValues = {
  setIsChangingCustomerNo: false
};

export default Details;
